import {
  DYNAMIC_FIELDS,
  DEFAULT_OFFER_TEXT,
  DEFAULT_PAUSE_ANSWERS,
  DEFAULT_CANCEL_ANSWERS,
} from "../interfaces/retention-workflow.interface";

/**
 * LocalStorage key for learn more banner dismissal
 */
export const RETENTION_WORKFLOWS_BANNER_DISMISSED_KEY =
  "retentionWorkflowsMessageDismissed";

/**
 * External documentation URLs
 */
export const RETENTION_WORKFLOWS_DOCS = {
  main: "https://docs.qpilot.cloud/docs/retain-and-grow",
  surveys: "https://docs.qpilot.cloud/docs/retain-and-grow#surveys",
  offers: "https://docs.qpilot.cloud/docs/retain-and-grow#offers",
};

/**
 * Action trigger enum values
 */
export const ACTION_TRIGGERS = {
  PAUSE: 0,
  CANCEL: 3,
} as const;

/**
 * Action trigger display names
 */
export const ACTION_TRIGGER_DISPLAY_NAMES: Record<number, string> = {
  [ACTION_TRIGGERS.PAUSE]: "Pause",
  [ACTION_TRIGGERS.CANCEL]: "Cancel",
};

/**
 * Status filter options for the list page
 */
export const STATUS_FILTER_OPTIONS = [
  { value: "enabled", label: "Enabled" },
  { value: "deleted", label: "Deleted" },
];

/**
 * Triggered by filter options for the list page
 */
export const TRIGGERED_BY_FILTER_OPTIONS = [
  { value: ACTION_TRIGGERS.PAUSE, label: "Pause" },
  { value: ACTION_TRIGGERS.CANCEL, label: "Cancel" },
];

/**
 * Maximum number of workflows allowed per site
 */
export const MAX_WORKFLOWS = 10;

/**
 * Form validation constraints
 * Note: Backend enforces 50 char max for name/question
 */
export const SURVEY_VALIDATION = {
  name: {
    minLength: 10,
    maxLength: 50,
  },
  question: {
    minLength: 10,
    maxLength: 50,
  },
  answer: {
    minLength: 10,
    maxLength: 200,
  },
  maxAnswers: 10,
  minAnswers: 1,
};

/**
 * Default survey question text
 */
export const DEFAULT_SURVEY_QUESTIONS = {
  pause: "Why would you like to pause your subscription?",
  cancel: "Why would you like to cancel your subscription?",
};

/**
 * Toast messages for survey actions
 */
export const SURVEY_TOAST_MESSAGES = {
  createSuccess: "Retention workflow created successfully",
  updateSuccess: "Retention workflow updated successfully",
  deleteSuccess: "Retention workflow deleted successfully",
  duplicateSuccess: "Retention workflow duplicated successfully",
  enableSuccess: "Retention workflow enabled successfully",
  disableSuccess: "Retention workflow disabled successfully",
  createError: "Failed to create retention workflow",
  updateError: "Failed to update retention workflow",
  deleteError: "Failed to delete retention workflow",
  duplicateError: "Failed to duplicate retention workflow",
  enableError: "Failed to enable retention workflow",
  disableError: "Failed to disable retention workflow",
  loadError: "Failed to load retention workflows",
  loadSingleError: "Failed to load retention workflow",
  maxWorkflowsReached:
    "You can only have 10 surveys; please delete one to add more",
};

/**
 * Max workflows warning message for UI
 */
export const MAX_WORKFLOWS_MESSAGE = {
  title: "Maximum Workflows Reached",
  description:
    "You have reached the maximum of 10 retention workflows. Please delete an existing workflow before creating a new one.",
};

/**
 * Confirmation dialog messages
 */
export const SURVEY_CONFIRM_MESSAGES = {
  delete: {
    header: "Delete Retention Workflow",
    message:
      "Are you sure you want to delete this retention workflow? This action cannot be undone.",
    acceptLabel: "Delete",
    rejectLabel: "Cancel",
  },
  disable: {
    header: "Disable Retention Workflow",
    message:
      "Are you sure you want to disable this retention workflow? Customers will no longer see this survey.",
    acceptLabel: "Disable",
    rejectLabel: "Cancel",
  },
};

/**
 * Learn more popover content
 */
export const LEARN_MORE_CONTENT = {
  title: "Automate Surveys and Offers with Retention Workflows",
  description:
    "Retention Workflows enable merchants to present win-back offers and gather churn data to boost retention and gain deeper insights.",
  learnMoreLabel: "Learn more",
};

// Re-export constants from interfaces for convenience
export {
  DYNAMIC_FIELDS,
  DEFAULT_OFFER_TEXT,
  DEFAULT_PAUSE_ANSWERS,
  DEFAULT_CANCEL_ANSWERS,
};
